import { z } from 'zod';

/**
 * Daily run-error analysis (docs/run-analysis.md). The report is built deterministically from
 * aggregated rows (no transcripts); `issues` are error groups by agent, tool, flow, trigger or task +
 * a normalized error signature. A second, capped model call adds a cause hypothesis and a proposal
 * to the top issues (`analysis`); the report exists without it when that call is skipped or fails.
 */

/** Outbox event written once per report when it is ready (the notification router renders it). */
export const RUN_ANALYSIS_READY_EVENT = 'run_analysis.ready';

/**
 * `pending` (waiting for the model step) → `running` → `done` | `partial` (some top issues got no
 * proposal) | `budget_exceeded` | `no_model` | `failed`. `not_needed`: nothing to analyse ("all ok").
 * `skipped`: reports made before the analysis existed.
 */
export const RUN_ANALYSIS_STATUSES = [
  'pending',
  'running',
  'done',
  'partial',
  'not_needed',
  'budget_exceeded',
  'no_model',
  'failed',
  'skipped',
] as const;
export type RunAnalysisStatus = (typeof RUN_ANALYSIS_STATUSES)[number];

export const RUN_ANALYSIS_CATEGORIES = [
  'agent_instruction',
  'tool_policy',
  'permission_or_integration',
  'tool_bug',
  'workflow',
  'infrastructure',
  'other',
] as const;
export type RunAnalysisCategory = (typeof RUN_ANALYSIS_CATEGORIES)[number];

/** Model-written hypothesis and proposal for one issue (from aggregated data only). */
export const RunAnalysisIssueAnalysis = z.object({
  cause: z.string(),
  proposal: z.string(),
  category: z.enum(RUN_ANALYSIS_CATEGORIES),
});
export type RunAnalysisIssueAnalysis = z.infer<typeof RunAnalysisIssueAnalysis>;

export const RUN_ANALYSIS_ISSUE_KINDS = [
  'run_failed',
  'run_cancelled',
  'run_recovered',
  'run_stuck',
  'run_outlier',
  'tool_error',
  'tool_denied',
  'tool_slow',
  'approval',
  'trigger_skipped',
  'trigger_disabled',
  'task_failed',
  'flow_failed',
] as const;
export type RunAnalysisIssueKind = (typeof RUN_ANALYSIS_ISSUE_KINDS)[number];

/** Time of day (org timezone) as `HH:MM`. */
export const RunAnalysisTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM (00:00–23:59)');

export const RunAnalysisSettings = z.object({
  enabled: z.boolean(),
  /** Local time (org timezone) from which the daily report is generated. */
  time: RunAnalysisTime,
  timezone: z.string(),
});
export type RunAnalysisSettings = z.infer<typeof RunAnalysisSettings>;
export const RunAnalysisSettingsPatch = z
  .object({ enabled: z.boolean(), time: RunAnalysisTime })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export type RunAnalysisSettingsPatch = z.infer<typeof RunAnalysisSettingsPatch>;

export const RunAnalysisStats = z.object({
  /** Runs created in the window. */
  runs: z.number(),
  /** Runs that ended in the window, by final status. */
  completed: z.number(),
  failed: z.number(),
  cancelled: z.number(),
  /** failed / (completed + failed + cancelled), 0 when nothing finished. */
  failureRate: z.number(),
  costUsd: z.number(),
  toolCalls: z.number(),
  toolErrors: z.number(),
  approvalsExpired: z.number(),
  approvalsDenied: z.number(),
  triggersSkipped: z.number(),
  triggersDisabled: z.number(),
  tasksFailed: z.number(),
  /** Number of issue groups, and how many are new / recurring compared to the previous report. */
  issueCount: z.number(),
  newIssues: z.number(),
  recurringIssues: z.number(),
  resolvedIssues: z.number(),
});
export type RunAnalysisStats = z.infer<typeof RunAnalysisStats>;

/** Null means unavailable or ambiguous; old reports may omit this field. */
export const RunAnalysisFailingStep = z
  .string()
  .max(40)
  .regex(/^[a-z][a-z0-9_]*$/)
  .nullable();

export const RunAnalysisIssue = z.object({
  /** Stable across days: kind + subject + signature; flow failures also include the step id (or unknown). */
  key: z.string(),
  kind: z.enum(RUN_ANALYSIS_ISSUE_KINDS),
  /** Agent, tool, flow, trigger or task assignee this group belongs to. */
  subjectType: z.enum(['agent', 'tool', 'flow', 'trigger']),
  subjectId: z.string(),
  subjectName: z.string(),
  /** Normalized, redacted error text (ids, timestamps and numbers removed, truncated). */
  signature: z.string(),
  failingStep: RunAnalysisFailingStep.optional(),
  count: z.number(),
  firstSeen: z.string(),
  lastSeen: z.string(),
  /** 1–3 runs that show the problem (link to /runs/<id>). */
  exampleRunIds: z.array(z.string()).max(3),
  /** Agents involved for tool/approval issues (top 3). */
  agents: z.array(z.object({ id: z.string(), name: z.string(), count: z.number() })).max(3),
  trend: z.enum(['new', 'recurring']),
  /** Cause hypothesis + concrete proposal; present on the analysed top issues once the model step ran. */
  analysis: RunAnalysisIssueAnalysis.optional(),
});
export type RunAnalysisIssue = z.infer<typeof RunAnalysisIssue>;

/** An issue of the previous report that did not occur in this window. */
export const RunAnalysisResolved = z.object({
  key: z.string(),
  kind: z.enum(RUN_ANALYSIS_ISSUE_KINDS),
  subjectName: z.string(),
  signature: z.string(),
  failingStep: RunAnalysisFailingStep.optional(),
});
export type RunAnalysisResolved = z.infer<typeof RunAnalysisResolved>;

export const RunAnalysisReportSummary = z.object({
  id: z.string(),
  /** Local calendar date of the org the report belongs to (one report per org per date). */
  reportDate: z.string(),
  periodStart: z.string(),
  periodEnd: z.string(),
  trigger: z.enum(['schedule', 'manual']),
  stats: RunAnalysisStats,
  summary: z.string(),
  /** Cost of the model step (0 when none ran); also visible per call in the usage records. */
  costUsd: z.number(),
  analysisStatus: z.enum(RUN_ANALYSIS_STATUSES),
  /** `provider:model` that wrote the proposals (null when no model call was made). */
  analysisModel: z.string().nullable(),
  /** Input + output tokens of the model step. */
  analysisTokens: z.number(),
  /** Short scrubbed reason when the status is `failed`, `budget_exceeded` or `no_model`. */
  analysisNote: z.string().nullable(),
  /** When the "report ready" notification was queued (null: not yet). */
  notifiedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type RunAnalysisReportSummary = z.infer<typeof RunAnalysisReportSummary>;

export const RunAnalysisReport = RunAnalysisReportSummary.extend({
  issues: z.array(RunAnalysisIssue),
  resolved: z.array(RunAnalysisResolved),
});
export type RunAnalysisReport = z.infer<typeof RunAnalysisReport>;

export const RunAnalysisListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(30),
  /** `reportDate` of the last item of the previous page. */
  cursor: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type RunAnalysisListQuery = z.infer<typeof RunAnalysisListQuery>;
